import { api } from "@/services/api"
import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"

// ── Types ────────────────────────────────────────────────────────────
// Mirror ScheduleOverrideModels.cs in Cms.WebApi. Enums arrive as numbers — this API serializes
// them that way — and are decoded by the tables in lib/overrideForm.ts.

/** AmsScheduleOverrideKind. */
export type OverrideKind = 0 | 1 | 2 | 3

/** AmsScheduleOverrideStatus. */
export type OverrideStatus = 0 | 1 | 2 | 3 | 4 | 5

/** AmsScheduleOverrideProjectionStatus. */
export type ProjectionStatus = 0 | 1 | 2 | 3 | 4

/** The event types an override can force, and that a projection or an override row can name. Private is AB#779's. */
export type EventTypeSlug = "intake" | "off-slot" | "private"

export interface CreateScheduleOverrideRequest {
  kind: OverrideKind
  /** "yyyy-MM-dd", in the sites' zone. */
  startDate: string
  /** "HH:mm:ss" on startDate. Midnight when left out. */
  startTime?: string
  /** Inclusive. */
  endDate: string
  /** "HH:mm:ss" on endDate. "23:59:00" — the end of the day — when left out. */
  endTime?: string
  /**
   * The site. Required for a site closure and a site event-type override; for the host kinds it narrows
   * the override to that site (out of office), or names the site a reassignment is made from.
   */
  teamSlug?: string
  /**
   * The target site of a temporary site reassignment (kind 0, AB#805): any other site where the host
   * holds a host record. Every other site of theirs is closed for the window.
   */
  outreachTeamSlug?: string
  hostEmail?: string
  eventTypeSlug?: EventTypeSlug
  note?: string
}

export interface ScheduleOverrideSummary {
  id: string
  kind: OverrideKind
  startDate: string
  /** "HH:mm:ss". */
  startTime: string
  endDate: string
  /** "HH:mm:ss". "23:59:00" stands for the end of the day. */
  endTime: string
  teamSlug: string | null
  teamName: string | null
  outreachTeamSlug: string | null
  outreachTeamName: string | null
  hostEmail: string | null
  hostName: string | null
  eventTypeSlug: EventTypeSlug | null
  note: string | null
  status: OverrideStatus
  createdBy: string
  createdAt: string
  revokedBy: string | null
  revokedAt: string | null
  /** Every site it wrote to. */
  affectedTeamSlugs: string[]
  appliedDays: number
  failedDays: number
  /** Days changed in Cal after it wrote them, which withdrawing it left alone. */
  conflictedDays: number
}

export interface ScheduleOverrideProjection {
  calScheduleId: number
  teamSlug: string
  teamName: string | null
  eventTypeSlug: EventTypeSlug | null
  isSiteSchedule: boolean
  date: string
  /** The part of the day it covers, "13:00-17:00", when that is not all of it. A span to midnight ends "00:00". */
  span: string | null
  /** "00:00-00:00" for closed, otherwise "09:00-10:00,10:30-12:00" — within span when there is one. */
  windows: string
  reason: string | null
  status: ProjectionStatus
  detail: string | null
}

export interface ScheduleOverrideDetail {
  override: ScheduleOverrideSummary
  projections: ScheduleOverrideProjection[]
  /**
   * The Booked appointments it makes somebody unavailable for, not yet started, in the calendar's
   * shape (AB#769) — read afresh on every request. Only for a caller with appointments:manage; null
   * otherwise, and for a withdrawn override. Empty for an event-type override; for a temporary site
   * reassignment, the host's at each site it closes them at (AB#805).
   */
  bookings?: BookingDetails[] | null
}

export interface ScheduleOverrideList {
  overrides: ScheduleOverrideSummary[]
  /** True when narrowed to the caller's own and their sites' overrides. */
  restricted: boolean
}

export interface ScheduleOverridePreviewLine {
  teamName: string
  target: string
  windows: string
  days: number
  firstDate: string
  lastDate: string
}

export interface ScheduleOverridePreview {
  errors: string[]
  warnings: string[]
  lines: ScheduleOverridePreviewLine[]
  /** The Booked appointments it would affect (AB#769). Null when it would be refused. */
  bookings?: BookingDetails[] | null
}

/** One overlap a reassignment's one-site-per-host refusal lists (HostSiteConflictDto, AB#805). */
export interface ScheduleOverrideHostSiteConflict {
  otherTeamSlug: string
  otherSiteName: string
  /** DayOfWeek, as a number. */
  weekday: number
  otherStart: string
  otherEnd: string
  start: string
  end: string
  /** The overlap as a sentence. */
  message: string
}

/**
 * The body of create's 409 when a reassignment would leave the host available at two sites at once
 * (SlotPlanConflictProblem, AmsHostSiteExclusivity on, AB#805): the same shape the slot-plan editor's refusal has.
 */
export interface ScheduleOverrideConflictProblem {
  code: "host-site-conflict"
  message: string
  conflicts: ScheduleOverrideHostSiteConflict[]
}

/** The body of both a 201 and a 400 from create: `override` is null exactly when it was refused. */
export interface CreateScheduleOverrideResult {
  override: ScheduleOverrideDetail | null
  errors: string[]
  warnings: string[]
  /** Each overlap, when the refusal is one site per host per slot (the controller then answers 409). Null otherwise. */
  hostSiteConflicts?: ScheduleOverrideHostSiteConflict[] | null
}

/** AmsBookingMoveBatchStatus: Queued 0, Running 1, Succeeded 2, Failed 3. */
export type BookingMoveBatchStatus = 0 | 1 | 2 | 3

/** AmsBookingMoveBatchItemStatus: Pending 0, Moved 1, Failed 2. */
export type BookingMoveBatchItemStatus = 0 | 1 | 2

/** One booking of a booking-move batch and what became of it (BookingMoveBatchStatusItemDto, AB#809). */
export interface BookingMoveBatchItem {
  id: string
  bookingRequestId: string
  /** The Cal booking the batch was given. */
  calBookingUid: string | null
  targetCalUserId: number
  targetHostEmail: string | null
  /** ISO instant. */
  targetStart: string
  status: BookingMoveBatchItemStatus
  /** Why it failed; the booking is in the review queue as "Reschedule failed". */
  failureDetail: string | null
  /** The replacement's Cal booking, once moved. */
  replacementBookingUid: string | null
}

/**
 * GET api/ams/booking-move-batches/{id} (BookingMoveBatchStatusDto, AB#809): a batch of automatic or
 * manual moves — its status, counts and timestamps, and each booking's result. Manage only.
 */
export interface BookingMoveBatch {
  id: string
  /** AmsBookingMoveBatchMode: Automatic 0, Manual 1. */
  mode: 0 | 1
  status: BookingMoveBatchStatus
  sourceOverrideId: string | null
  requestedAt: string
  startedAt: string | null
  finishedAt: string | null
  attempts: number
  totalCount: number
  movedCount: number
  failedCount: number
  pendingCount: number
  items: BookingMoveBatchItem[]
}

/** One way of resolving an override's affected bookings, and why it is not offered (ScheduleOverrideResolveOption, AB#810). */
export interface ScheduleOverrideResolveOption {
  enabled: boolean
  /** Why it is not offered, for staff; null when it is. */
  reason: string | null
}

/**
 * GET api/ams/schedule-overrides/{id}/resolve/options (ScheduleOverrideResolveOptions, AB#810): which ways
 * of resolving the override's affected bookings the WebUI offers, and why not. Manage only.
 */
export interface ScheduleOverrideResolveOptions {
  automatic: ScheduleOverrideResolveOption
  manual: ScheduleOverrideResolveOption
  /** How many Booked appointments the override affects now. */
  affectedCount: number
  /** The public booking window (28 days). */
  lookaheadDays: number
  /** How many days past the window automatic reassignment searches. */
  horizonDays: number
  /** A booking-move batch for this override still queued or running, to follow. */
  activeBatchId: string | null
}

/**
 * GET api/ams/schedule-overrides/resolve/capabilities (ScheduleOverrideResolveCapabilities, AB#810): which
 * ways of resolving an override's affected bookings are open at all, from the server's settings alone —
 * what the resolution choice shows before the override is saved. Manage only; 404 with the week view off.
 */
export interface ScheduleOverrideResolveCapabilities {
  automatic: ScheduleOverrideResolveOption
  manual: ScheduleOverrideResolveOption
  /** The public booking window (28 days). */
  lookaheadDays: number
  /** How many days past the window automatic reassignment searches. */
  horizonDays: number
  /** Cal's minimum booking notice in minutes (480), from AB#811; optional for servers before M15. */
  minimumNoticeMinutes?: number
}

/** One booking of an automatic reassignment plan (AutomaticReassignmentPreviewItemDto, AB#810). */
export interface AutomaticReassignmentPreviewItem {
  requestId: string
  calBookingUid: string
  hostCalUserId: number | null
  teamSlug: string | null
  eventTypeSlug: string | null
  /** ISO instants. */
  currentStart: string
  currentEnd: string
  /** "yyyy-MM-dd", Pacific; null when no date fits. */
  targetDate: string | null
  targetStart: string | null
  targetEnd: string | null
  /** Why it cannot be moved; null when it can. */
  reason: string | null
}

/**
 * POST api/ams/schedule-overrides/{id}/resolve/automatic/preview (AutomaticReassignmentPreviewDto, AB#810):
 * where each affected booking would move, or why it cannot. Changes nothing.
 */
export interface AutomaticReassignmentPreview {
  overrideId: string
  /** "yyyy-MM-dd": the Pacific date the plan was made on. */
  today: string
  /** The earliest date searched: the first day past the booking window. */
  firstDate: string
  /** The latest date searched. */
  lastDate: string
  horizonDays: number
  planned: number
  unplaceable: number
  items: AutomaticReassignmentPreviewItem[]
}

/** The 202 body of POST api/ams/schedule-overrides/{id}/resolve/automatic (AutomaticReassignmentAccepted, AB#810). */
export interface AutomaticReassignmentAccepted {
  batchId: string
  planned: number
  unplaceable: number
}

/** A Pacific wall-clock stretch of one day, `[start, end)`: "08:30:00" (AutoReassignmentWindow). */
export interface ManualResolveWindow {
  start: string
  end: string
}

/** A Booked appointment a host already has that day, at any site (ManualReassignmentBusyDto, AB#811). */
export interface ManualResolveBusy {
  requestId: string
  /** ISO instant. */
  start: string
  /** ISO instant; null when not yet known (the server counts it as one minute). */
  end: string | null
}

/** One host of the manual day read (ManualReassignmentDayHostDto, AB#811). */
export interface ManualResolveDayHost {
  calUserId: number
  name: string | null
  email: string | null
  /** Their Booked appointments that day, at every site. */
  booked: number
  /** Their daily cap at this site that day. Null is tolerated as "no cap", though the server always sends one. */
  limit: number | null
  /** Whether `booked` has reached `limit`. */
  capped: boolean
  /**
   * By slot type (event type slug), the windows they are effectively available in at this site: the site
   * offers the type, they are available, overrides counted, breaks excluded.
   */
  available: Record<string, ManualResolveWindow[]>
  /**
   * Their Booked appointments that day at every site — including ones paired to move away in this batch,
   * which still count where they are: never free them client-side.
   */
  busy: ManualResolveBusy[]
  /**
   * Rule 1 (one site per host): by each other site they host at (team slug), the windows they are
   * effectively available in there that day, any slot type they host there. A target must overlap none.
   * Absent or empty when they host nowhere else.
   */
  elsewhere?: Record<string, ManualResolveWindow[]>
}

/**
 * GET api/ams/schedule-overrides/{id}/resolve/manual/day?teamSlug=&date= (ManualReassignmentDayDto, AB#811):
 * what the manual reschedule workspace needs to mark one site's valid slots on one Pacific date. Manage
 * only; 404 with the week view off. Rule 1 (one site per host) is in each host's `elsewhere`; the batch
 * re-checks it against Cal at run time.
 */
export interface ManualResolveDay {
  overrideId: string
  teamSlug: string
  /** "yyyy-MM-dd", Pacific. */
  date: string
  /** "yyyy-MM-dd": today, Pacific. */
  today: string
  /** ISO instant: now plus Cal's minimum notice — the earliest start a move may have. */
  earliestStart: string
  /** "yyyy-MM-dd": the last day of the 28-day booking window. */
  windowLastDate: string
  /** "yyyy-MM-dd": the last day a move may reach (the window plus AutoReassignment:HorizonDays). */
  lastDate: string
  minimumNoticeMinutes: number
  lookaheadDays: number
  horizonDays: number
  hosts: ManualResolveDayHost[]
  /**
   * Whether a move must keep the booking's own slot type. False from the server (decision 2026-10-28,
   * ams-8ia): a target may lie in a window of any of the site's types and the booking takes that type.
   * Absent (an older server) reads as true. The site must still be the booking's own.
   */
  sameSlotTypeRequired?: boolean
  /**
   * By slot type (event type slug), how long the site's appointments of that type are: a move onto
   * another type runs that type's length. A type with no entry keeps the booking's own length.
   */
  eventTypeMinutes?: Record<string, number>
}

/** The arguments of the manual day read. */
export interface ManualResolveDayArgs {
  overrideId: string
  teamSlug: string
  /** "yyyy-MM-dd", Pacific. */
  date: string
}

/** One manual move (ManualReassignmentPairing, AB#811): which affected booking, and the host, start and slot type it moves to. */
export interface ManualResolvePairing {
  bookingRequestId: string
  targetCalUserId: number
  /** ISO instant. */
  targetStart: string
  /**
   * The slot type (event type slug) of the cell picked, which may differ from the booking's own (ams-8ia).
   * Omitted lets the server read it off the host's day.
   */
  targetEventTypeSlug?: string
}

/** The arguments of POST api/ams/schedule-overrides/{id}/resolve/manual: the body is `{ pairings }`. */
export interface ManualResolveRequest {
  overrideId: string
  pairings: ManualResolvePairing[]
}

/** The 202 body of POST …/resolve/manual (ManualReassignmentAccepted, AB#811). */
export interface ManualResolveAccepted {
  batchId: string
  planned: number
}

/** Why one pairing was refused (ManualReassignmentPairingError, AB#811). */
export interface ManualResolvePairingError {
  bookingRequestId: string
  reason: string
}

/** The 400 body of POST …/resolve/manual when any pairing is refused (ManualReassignmentErrors): nothing was queued. */
export interface ManualResolveErrors {
  errors: ManualResolvePairingError[]
}

// ── Endpoints ────────────────────────────────────────────────────────

const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsScheduleOverrides", "AmsBookings", "AmsBookingMoveBatches"] })

export const scheduleOverridesApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    getScheduleOverrides: builder.query<ScheduleOverrideList, { includePast: boolean; includeRevoked: boolean }>({
      query: (params) => ({ url: "/api/ams/schedule-overrides", params }),
      providesTags: ["AmsScheduleOverrides"],
    }),
    // The detail lists the bookings the override affects (AB#769), so cancelling or moving one of
    // them — which invalidates "AmsBookings" — asks for it again, and the list empties as they go.
    getScheduleOverride: builder.query<ScheduleOverrideDetail, string>({
      query: (id) => ({ url: `/api/ams/schedule-overrides/${encodeURIComponent(id)}` }),
      providesTags: ["AmsScheduleOverrides", "AmsBookings"],
    }),
    /**
     * A dry run: what would be written, or why it would be refused, and the bookings it would affect.
     * Writes nothing, so it is a query despite the POST — and provides "AmsBookings", so releasing or
     * moving one of its bookings asks for it again, as staffMovesApi's preview does.
     */
    previewScheduleOverride: builder.query<ScheduleOverridePreview, CreateScheduleOverrideRequest>({
      query: (body) => ({ url: "/api/ams/schedule-overrides/preview", method: "POST", body }),
      providesTags: ["AmsBookings"],
      // Each preview describes the form as it is now and goes stale with the clock; never served from an old one.
      keepUnusedDataFor: 0,
    }),
    // Both writes change what Cal publishes, which is what the calendar's availability read shows —
    // so they drop that cache too.
    createScheduleOverride: builder.mutation<CreateScheduleOverrideResult, CreateScheduleOverrideRequest>({
      query: (body) => ({ url: "/api/ams/schedule-overrides", method: "POST", body }),
      invalidatesTags: ["AmsScheduleOverrides", "AmsBookings"],
    }),
    revokeScheduleOverride: builder.mutation<ScheduleOverrideDetail, string>({
      query: (id) => ({ url: `/api/ams/schedule-overrides/${encodeURIComponent(id)}`, method: "DELETE" }),
      invalidatesTags: ["AmsScheduleOverrides", "AmsBookings"],
    }),
    // A booking-move batch's progress (AB#809), polled by useBookingMoveBatch until it finishes; the
    // "booking-move-batch" notification drops it so an open page sees the end at once.
    getBookingMoveBatch: builder.query<BookingMoveBatch, string>({
      query: (id) => ({ url: `/api/ams/booking-move-batches/${encodeURIComponent(id)}` }),
      providesTags: (_result, _error, id) => [{ type: "AmsBookingMoveBatches" as const, id }],
    }),
    // Automatic reassignment (AB#810). The options say whether it can be offered (and follow a batch
    // already under way); they change as bookings and overrides do.
    getResolveOptions: builder.query<ScheduleOverrideResolveOptions, string>({
      query: (id) => ({ url: `/api/ams/schedule-overrides/${encodeURIComponent(id)}/resolve/options` }),
      providesTags: ["AmsScheduleOverrides", "AmsBookings"],
    }),
    // What the resolution choice offers before the override exists (AB#810): settings only, so it
    // changes only with a deploy and needs no tags.
    getResolveCapabilities: builder.query<ScheduleOverrideResolveCapabilities, void>({
      query: () => ({ url: "/api/ams/schedule-overrides/resolve/capabilities" }),
    }),
    // The plan changes nothing, but it is a POST the server audits and it goes stale with the clock, so
    // it is asked for on purpose, once per look — a mutation, never cached.
    previewAutomaticResolution: builder.mutation<AutomaticReassignmentPreview, string>({
      query: (id) => ({ url: `/api/ams/schedule-overrides/${encodeURIComponent(id)}/resolve/automatic/preview`, method: "POST" }),
    }),
    // Queues the batch: 202 with its id. Unplaceable bookings go to the review queue at once.
    runAutomaticResolution: builder.mutation<AutomaticReassignmentAccepted, string>({
      query: (id) => ({ url: `/api/ams/schedule-overrides/${encodeURIComponent(id)}/resolve/automatic`, method: "POST" }),
      invalidatesTags: (result) => [
        "AmsScheduleOverrides",
        "AmsBookings",
        ...(result ? [{ type: "AmsBookingMoveBatches" as const, id: result.batchId }] : []),
      ],
    }),
    // The manual reschedule workspace's pairings (AB#811): validated up front (400 with a reason per
    // refused pairing, nothing queued) and queued as a Manual batch — 202 with its id.
    runManualResolution: builder.mutation<ManualResolveAccepted, ManualResolveRequest>({
      query: ({ overrideId, pairings }) => ({
        url: `/api/ams/schedule-overrides/${encodeURIComponent(overrideId)}/resolve/manual`,
        method: "POST",
        body: { pairings },
      }),
      invalidatesTags: (result) => [
        "AmsScheduleOverrides",
        "AmsBookings",
        ...(result ? [{ type: "AmsBookingMoveBatches" as const, id: result.batchId }] : []),
      ],
    }),
    // The manual reschedule workspace's day (AB#811): each host's load, cap, windows and appointments at
    // one site on one date, and the rule constants. Changes as bookings and overrides do.
    getManualResolveDay: builder.query<ManualResolveDay, ManualResolveDayArgs>({
      query: ({ overrideId, teamSlug, date }) => ({
        url: `/api/ams/schedule-overrides/${encodeURIComponent(overrideId)}/resolve/manual/day`,
        params: { teamSlug, date },
      }),
      providesTags: ["AmsBookings", "AmsScheduleOverrides"],
    }),
  }),
})

export const {
  useGetScheduleOverridesQuery,
  useGetScheduleOverrideQuery,
  usePreviewScheduleOverrideQuery,
  useCreateScheduleOverrideMutation,
  useRevokeScheduleOverrideMutation,
  useGetBookingMoveBatchQuery,
  useGetResolveOptionsQuery,
  useLazyGetResolveOptionsQuery,
  useGetResolveCapabilitiesQuery,
  usePreviewAutomaticResolutionMutation,
  useRunAutomaticResolutionMutation,
  useGetManualResolveDayQuery,
  useRunManualResolutionMutation,
} = scheduleOverridesApi

/**
 * The manual day read for one site and date, skipped until all three are known — a page still mapping
 * the override's site, or with no booking active, asks for nothing. `currentData` only, so a step to the
 * next day never marks it with the previous day's facts. `pollingInterval` re-reads it on a timer, so the
 * "now" it carries (earliestStart) does not go stale on a page left open.
 */
export function useManualDay(
  overrideId: string | null | undefined,
  teamSlug: string | null | undefined,
  date: string | null | undefined,
  options: { pollingInterval?: number } = {},
) {
  const query = useGetManualResolveDayQuery(
    { overrideId: overrideId ?? "", teamSlug: teamSlug ?? "", date: date ?? "" },
    { skip: !overrideId || !teamSlug || !date, pollingInterval: options.pollingInterval ?? 0 },
  )
  return { ...query, day: query.currentData }
}
